/**
 * The web app's "Get the Neosocial app for Windows" banner (2026-11-01): who
 * sees it, and where its Download button points. The rules live here, pure
 * and tested (test/desktop-download.test.tsx); the bar is
 * shell/DesktopDownloadBar.tsx.
 *
 * It shows only when all of these hold:
 * - on the web: never inside the desktop app (its bridge is there);
 * - on a Windows desktop browser: not phones, tablets, macOS or Linux;
 * - the console's `desktop_download_banner` flag is on (the default);
 * - a release is published: `/download/latest.json` (the updater's manifest,
 *   docs/build/desktop-release.md) names a Windows build;
 * - its close (X) was not pressed on this browser in the last 30 days.
 *
 * The Download button points at the manifest's `download_url` when it has a
 * safe one (so it can change on the server, without a deploy), else at the
 * stable `/download/Neosocial-Setup.exe`.
 */
import { storage } from './storage';

export const DOWNLOAD_MANIFEST_PATH = '/download/latest.json';
export const DOWNLOAD_STABLE_PATH = '/download/Neosocial-Setup.exe';
/** The close (X) hides the banner for this long on this browser. */
export const DOWNLOAD_SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;
export const DOWNLOAD_DISMISSED_KEY = 'desktopDownload.dismissedAt';
/** The updater's platform key for the Windows build (Tauri's static JSON). */
export const WINDOWS_PLATFORM = 'windows-x86_64';

const SEMVER = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/;

/** What the browser says about the device. */
export interface BrowserHints {
  userAgent: string;
  /** `navigator.userAgentData.platform`, where the browser has it (Chromium). */
  uaPlatform?: string;
  /** `navigator.userAgentData.mobile`. */
  uaMobile?: boolean;
}

interface UaData {
  platform?: unknown;
  mobile?: unknown;
}

export function browserHints(
  nav: (Navigator & { userAgentData?: UaData }) | undefined = globalThis.navigator,
): BrowserHints {
  const data = nav?.userAgentData;
  return {
    userAgent: nav?.userAgent ?? '',
    uaPlatform: typeof data?.platform === 'string' ? data.platform : undefined,
    uaMobile: typeof data?.mobile === 'boolean' ? data.mobile : undefined,
  };
}

/**
 * A Windows desktop or laptop browser. Client hints win where the browser has
 * them (they are not changed by "Request desktop site" on a phone); else the
 * user agent: "Windows NT", and not a phone, Xbox or another device that
 * borrows the token.
 */
export function isWindowsDesktopBrowser(h: BrowserHints): boolean {
  if (h.uaMobile === true) return false;
  if (h.uaPlatform) return h.uaPlatform === 'Windows';
  const ua = h.userAgent;
  return /\bWindows NT \d/.test(ua) && !/Windows Phone|Mobile|Android|Xbox|iPhone|iPad/i.test(ua);
}

/** The close (X) was pressed less than 30 days ago (a time far ahead is a broken clock: ignored). */
export function isSnoozed(dismissedAt: number | null, now: number): boolean {
  if (dismissedAt === null || !Number.isFinite(dismissedAt)) return false;
  if (dismissedAt > now + DOWNLOAD_SNOOZE_MS) return false;
  return now - dismissedAt < DOWNLOAD_SNOOZE_MS;
}

/** Every rule but "a release is published", which needs the network. */
export function shouldOfferDownload(o: {
  hints: BrowserHints;
  inDesktopApp: boolean;
  flagOn: boolean;
  dismissedAt: number | null;
  now: number;
}): boolean {
  return (
    !o.inDesktopApp &&
    o.flagOn &&
    isWindowsDesktopBrowser(o.hints) &&
    !isSnoozed(o.dismissedAt, o.now)
  );
}

/** A published Windows release, as the banner needs it. */
export interface DownloadRelease {
  version: string;
  /** Where Download points (absolute). */
  url: string;
}

/**
 * A download link from the manifest, made absolute: https anywhere, or any
 * URL on the page's own origin. Never `javascript:`, `data:` or credentials.
 */
export function safeDownloadUrl(raw: unknown, pageOrigin: string): string | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  let u: URL;
  let page: URL;
  try {
    page = new URL(pageOrigin);
    u = new URL(raw.trim(), page);
  } catch {
    return null;
  }
  if (u.username || u.password) return null;
  if (u.protocol !== 'https:' && u.origin !== page.origin) return null;
  return u.href;
}

/**
 * The release `latest.json` announces, or null when it names no Windows
 * build. Tauri's updater ignores the extra `download_url` field.
 */
export function releaseFromManifest(raw: unknown, pageOrigin: string): DownloadRelease | null {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as { version?: unknown; platforms?: unknown; download_url?: unknown };
  if (typeof m.version !== 'string' || !SEMVER.test(m.version)) return null;
  const platforms = m.platforms && typeof m.platforms === 'object' ? m.platforms : null;
  const win = platforms ? (platforms as Record<string, unknown>)[WINDOWS_PLATFORM] : null;
  if (!win || typeof win !== 'object' || typeof (win as { url?: unknown }).url !== 'string') {
    return null;
  }
  let fallback: string;
  try {
    fallback = new URL(DOWNLOAD_STABLE_PATH, pageOrigin).href;
  } catch {
    return null;
  }
  return { version: m.version, url: safeDownloadUrl(m.download_url, pageOrigin) ?? fallback };
}

/**
 * Asks the web server for the manifest. Null when there is none: a 404, the
 * dev server's SPA fallback (HTML, not JSON), no network.
 */
export async function fetchDownloadRelease(
  pageOrigin: string,
  fetchFn: typeof fetch = globalThis.fetch,
): Promise<DownloadRelease | null> {
  try {
    const r = await fetchFn(new URL(DOWNLOAD_MANIFEST_PATH, pageOrigin).href, {
      cache: 'no-cache',
      credentials: 'omit',
    });
    if (!r.ok || !/json/i.test(r.headers.get('content-type') ?? '')) return null;
    return releaseFromManifest(await r.json(), pageOrigin);
  } catch {
    return null;
  }
}

/** When this browser last closed the banner (epoch ms), or null. */
export function readDismissedAt(): number | null {
  const raw = storage.get(DOWNLOAD_DISMISSED_KEY);
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function writeDismissedAt(at: number): void {
  storage.set(DOWNLOAD_DISMISSED_KEY, String(at));
}
